import { forwardRef, useRef } from 'react';
import type { AriaRole, HTMLAttributes } from 'react';
import classNames from 'classnames';
import { DropdownItem, DropdownItemProps } from '@components/dropdown/DropdownItem';
import './DropdownList.scss';

export interface DropdownListProps
  extends Omit<HTMLAttributes<HTMLUListElement>, 'id' | 'className' | 'role' | 'children'> {
  /** array of data objects to generate DropdownItems */
  items?: Array<DropdownItemProps>;
  /** change size of layout for desktop and mobile. default large */
  size?: 'large' | 'small';
  /** id for aria-controls */
  id?: string;
  /** boolean indicating if list should be shown */
  isOpen?: boolean;
  /** if true, will truncate item labels. Default true. */
  isProfileDropdown?: boolean;
  /** additional class names for the dropdown list */
  className?: string;
  /** ARIA role for the dropdown list */
  role?: AriaRole;
  linkComponent?: React.ElementType;
}
/**
 * DropdownList Component
 * @param items - array of data objects to generate DropdownItems
 * @param size - change size of layout for desktop and mobile. default large
 * @param id - id for aria-controls
 * @param isOpen - boolean indicating if list should be shown
 * @param ref - optional ref, used in DropdownTemplate for keyboard navigation
 * @param isProfileDropdown - if true, will truncate item labels. Default true.
 * @param className - any additional classname to the component.
 * @param role - ARIA role for the dropdown list.
 * @param linkComponent - Custom component to render links instead of a native `<a>` (e.g. Next.js Link, React Router Link).
 * @param rest - other props passed to the ul element.
 * @constructor
 */
export const DropdownList = forwardRef<HTMLUListElement, DropdownListProps>(
  (
    {
      id,
      items = [],
      size = 'large',
      isOpen = false,
      isProfileDropdown = true,
      className,
      role,
      linkComponent,
      ...rest
    },
    ref,
  ) => {
    const ulRef = useRef<HTMLUListElement | null>(null);

    const initRefs = (element: HTMLUListElement) => {
      ulRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <ul
        id={id}
        className={classNames(
          'vds-dropdown-list',
          `vds-dropdown-list--${size}`,
          isOpen ? 'vds-dropdown-list--open' : '',
          className,
        )}
        ref={initRefs}
        role={role}
        {...rest}>
        {items.map((item, index) => (
          <DropdownItem
            className="vds-dropdown-list__item"
            key={`item-${index}`}
            size={size}
            topDivider={index === 0}
            isTruncated={isProfileDropdown}
            linkComponent={linkComponent}
            {...item}
          />
        ))}
      </ul>
    );
  },
);
